import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import {
  PAGE_SIZE,
  getRandomHot,
  paginate,
  searchMovies,
  totalPages,
} from "@/utils/catalog";

const hotKeywords = ["剧情", "悬疑", "家庭", "自然", "治愈", "犯罪", "纪录片"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const pageParam = Number(searchParams.get("page") ?? "1");
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const [input, setInput] = useState(query);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setInput(query);
  }, [query]);

  usePageMeta(
    query ? `${query} 的搜索结果 - 黄色片免费看` : "影片搜索 - 黄色片免费看",
    query
      ? `在黄色片免费看中搜索“${query}”相关的影片资源，共找到匹配结果，可按分类继续浏览。`
      : "输入影片标题、导演或标签，快速查找你想看的免费影片资源。",
  );

  const results = useMemo(() => searchMovies(query), [query]);
  const pageCount = totalPages(results.length, PAGE_SIZE);
  const current = paginate(results, Math.min(page, pageCount), PAGE_SIZE);
  const hot = getRandomHot(4);

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setTouched(true);
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: value });
  };

  const changePage = (next: number) => {
    setSearchParams(next > 1 ? { q: query, page: String(next) } : { q: query });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const quickSearch = (word: string) => {
    setInput(word);
    setSearchParams({ q: word });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <section className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">搜索</span>
            </nav>
            <h1 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {query ? "搜索结果" : "影片搜索"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              输入影片标题、导演或标签关键词，我们会从全部影片资源中为你匹配。
            </p>

            <form onSubmit={submit} className="mt-4 flex w-full flex-col gap-2 sm:flex-row">
              <div className="box-border flex w-full min-w-0 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2">
                <i className="ri-search-line text-base text-foreground-500" />
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="例如：夜行列车、纪录片、悬疑"
                  aria-label="搜索关键词"
                  className="min-w-0 flex-1 bg-transparent py-1 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {input && (
                  <button
                    type="button"
                    aria-label="清空关键词"
                    onClick={() => setInput("")}
                    className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                  >
                    <i className="ri-close-circle-fill text-sm" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-search-eye-line text-base" /> 搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-foreground-500">大家在搜</span>
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => quickSearch(word)}
                  className="rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  {word}
                </button>
              ))}
            </div>
          </section>

          {query && results.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-foreground-600">
                关键词 <span className="font-medium text-primary-600">“{query}”</span> 共找到{" "}
                <span className="font-medium text-foreground-950">{results.length}</span> 部影片
              </p>
              <span className="text-xs text-foreground-400">
                第 {Math.min(page, pageCount)} / {pageCount} 页 · 每页 {PAGE_SIZE} 条
              </span>
            </div>
          )}

          {query && results.length > 0 && (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
                {current.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination page={Math.min(page, pageCount)} total={pageCount} onChange={changePage} />
            </>
          )}

          {!query && (
            <section className="mt-6">
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/40 px-6 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
                  <i className="ri-search-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {touched ? "请输入搜索关键词" : "还没有输入关键词"}
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-500">
                  想找什么？可以试试上方「大家在搜」的热门关键词，或者直接按分类浏览全部资源。
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
                  <Link
                    to="/category/movie"
                    className="rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    浏览电影分类
                  </Link>
                  <Link
                    to="/"
                    className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                  >
                    返回首页
                  </Link>
                </div>
              </div>

              <div className="mt-8">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  热门影片推荐
                </h2>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
                  {hot.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {query && results.length === 0 && (
            <section className="mt-6">
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/40 px-6 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
                  <i className="ri-file-search-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  未找到相关影片
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-500">
                  没有匹配到“{query}”的影片，试着换个关键词，或缩短搜索词再试一次。
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      setInput("");
                      setSearchParams({});
                    }}
                    className="rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置搜索
                  </button>
                  <Link
                    to="/"
                    className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                  >
                    返回首页
                  </Link>
                </div>
              </div>

              <div className="mt-8">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  换个方向看看
                </h2>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
                  {hot.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}